import { AfterViewInit, Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserSettings } from '../../../models/settings.model';
import { I18nService } from '../../../services/i18n.service';
import { UiService } from '../../../services/ui.service';
import { SettingsService } from '../../../services/settings.service';
import { MoreInfoComponent } from '../../../components/more-info/more-info';
import { NoteForUsResidentsComponent } from '../../../components/note-for-us-residents/note-for-us-residents';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { FormsModule } from '@angular/forms';

import { ChangePassword } from '../../change-password/change-password';
import { DataProtectionComponent } from '../../../components/data-protection/data-protection';
import { HeaderComponent } from '../../../components/header/header';
import { ImprintComponent } from '../../../components/imprint/imprint';
import { TechnicalDocumentSubpage } from '../technical-documents-subpage/technical-documents-subpage';
import { InputLabelComponent } from '../../../components/input-label/input-label';

import { NavButtonComponent } from '../../../components/nav-button/nav-button';
import { ServiceConditions } from '../../service-conditions/service-conditions';
import { TermsOfUseComponent } from '../../../components/terms-of-use/terms-of-use';
import { GLOBAL_CONSTANT } from '../../../constants/global.constant';
import { NotificationsSettingsSubpageComponent } from '../notifications-subpage/notifications-subpage';
import { AllNotificationsSettingsComponent } from '../all-notifications-settings/all-notifications-settings';
import { Equipment_Type, SettingSubpageEnums } from '../../../enums/global.enum';
import { I18N_KEYS, SupportedLanguage } from '../../../enums/i18.enum';
import { LP_UI_CONFIG } from '../../../core/tokens';

@Component({
  selector: 'app-settings-subpage',
  templateUrl: './settings-subpage.html',
  styleUrls: ['./settings-subpage.scss'],
  imports: [
    // NG-ZORRO Modules
    NzButtonModule,
    NzCollapseModule,
    NzIconModule,
    NzImageModule,
    NzInputModule,
    NzRadioModule,
    FormsModule,
    NzSpinModule,

    // Child Components
    HeaderComponent,
    InputLabelComponent,
    NavButtonComponent,
    ChangePassword,
    NotificationsSettingsSubpageComponent,

    // TechnicalDocumentsSubpageComponent,
    MoreInfoComponent,
    AllNotificationsSettingsComponent,
    ImprintComponent,
    DataProtectionComponent,
    ServiceConditions,
    TermsOfUseComponent,
    NoteForUsResidentsComponent,
    TechnicalDocumentSubpage,
  ],
})
export class SettingsSubpageComponent implements OnInit, AfterViewInit {
  private route = inject(ActivatedRoute);
  public router = inject(Router);
  public ui = inject(UiService);
  private settingsService = inject(SettingsService);
  public i18n = inject(I18nService);
  public readonly cfg = inject(LP_UI_CONFIG);
  public url = signal<string>(GLOBAL_CONSTANT.EMPTY_STRING);
  public test = signal<boolean>(false);
  public faqQuestions = signal<boolean[]>([false, false, false]);
  public settings = signal<UserSettings | undefined>(undefined);
  public settingsCopyForProfile = signal<UserSettings | undefined>(undefined);
  public changes = signal<boolean>(false);
  public loading = signal<boolean>(false);
  public success = signal<boolean>(false);
  public error = signal<boolean>(false);

  public firstParameter = signal<any>(GLOBAL_CONSTANT.EMPTY_STRING);

  public availableLanguages = signal<string[]>(['English']);
  public selectedLanguage = signal<string>(this.availableLanguages()[0]);
  public isMobile = this.ui.IsMobile;
  public containName = false;
  public containSurname = false;
  constructor() {
    effect(() => {
      this.ui.setBackText(this.i18n.langString('settings'));
    });
  }

  public backRoute = computed(
    () => '/settings' + (this.firstParameter() ? '/' + this.url() : GLOBAL_CONSTANT.EMPTY_STRING)
  );

  ngOnInit() {
    this.refresh();
    this.ui.updateBackBtn(false);
    this.ui.setBackText(this.i18n.langString('settings'));
  }

  ngAfterViewInit(): void {
    this.ui.updateTabIndex(this.route.snapshot.data['tabIndex']);
    this.ui.updateBackBtn(this.route.snapshot.data['backBtn']);
  }

  /**
   * Handle language selection change
   */
  handleLanguageChange(languageId: number) {
    const currentSettings = this.settings();
    if (!currentSettings) return;

    // Create a new settings object to avoid mutating the signal value
    const updatedSettings: UserSettings = {
      ...currentSettings,
      languageID: languageId,
    };

    this.handleSettingsChange(updatedSettings);
  }

  /**
   * Handle measurement system change
   */
  handleMeasurementSystemChange(measurementSystemId: number) {
    const currentSettings = this.settings();
    if (!currentSettings) return;

    // Create a new settings object with updated measurement system
    const updatedSettings: UserSettings = {
      ...currentSettings,
      measurementSystemID: measurementSystemId,
    };

    this.handleSettingsChange(updatedSettings);
  }

  /**
   * Handle date format change
   */
  handleDateFormatChange(dateFormatId: number) {
    const currentSettings = this.settings();
    if (!currentSettings) return;

    // Create a new settings object with updated date format
    const updatedSettings: UserSettings = {
      ...currentSettings,
      dateFormatID: dateFormatId,
    };

    this.handleSettingsChange(updatedSettings);
  }

  /**
   * Handle date format change
   */
  handleSettingsChange(updatedSettings: UserSettings) {
    const oldSettings = this.settings();
    // Check if there are any changes compared to the original settings
    if (JSON.stringify(oldSettings) === JSON.stringify(updatedSettings)) {
      this.success.set(false);
      this.error.set(false);
      return; // No changes, do not proceed
    }

    this.settingsService
      .setUserSettings(updatedSettings)
      .then(() => {
        this.success.set(true);
        this.error.set(false);
        // Refresh to get updated settings from store
        setTimeout(() => this.refresh(), 100);
      })
      .catch((error) => {
        console.error('Failed to update settings:', error);
        this.error.set(true);
        this.success.set(false);
      });
  }

  async refresh() {
    const urlParts = this.router.url.replace('/settings/', GLOBAL_CONSTANT.EMPTY_STRING).split('/');
    this.url.set(urlParts[0]);
    this.firstParameter.set(urlParts.length > 1 ? urlParts[1] : GLOBAL_CONSTANT.EMPTY_STRING);
    this.loading.set(true);

    // Ensure settings are loaded from API before reading
    const newSettings = await this.settingsService.getUserSettings();
    this.containName = newSettings.name ? true : false;
    this.containSurname = newSettings.surname ? true : false;

    this.settingsCopyForProfile.set(
      Object.assign({} as UserSettings, {
        ...newSettings,
      })
    );
    this.settings.set(newSettings);
    this.loading.set(false);
  }

  getTitle() {
    switch (this.url()) {
      case SettingSubpageEnums.Profile:
        return this.i18n.langString(I18N_KEYS.PROFILE);
      case SettingSubpageEnums.ChangePassword:
        return this.i18n.langString(I18N_KEYS.CHANGEPASSWORD);
      case SettingSubpageEnums.Language:
        return this.i18n.langString(I18N_KEYS.LANGUAGE);
      case SettingSubpageEnums.Metric:
        return this.i18n.langString(I18N_KEYS.MEASUREMENTSYSTEM);
      case SettingSubpageEnums.Appearance:
        return this.i18n.langString(I18N_KEYS.APPEARANCE);
      case SettingSubpageEnums.Notifications:
        return this.i18n.langString(I18N_KEYS.NOTIFICATIONS + this.firstParameter());
      case SettingSubpageEnums.TechnicalDocuments:
        return this.firstParameter() === Equipment_Type.FLANGE
          ? this.i18n.langString(I18N_KEYS.TECHNICALDOCFLANGE)
          : this.firstParameter() === Equipment_Type.STEAMTRAP
            ? this.i18n.langString(I18N_KEYS.TECHNICALDOCSTEAMTRAP)
            : this.firstParameter() === 'rotatingequipment'
              ? this.i18n.langString(I18N_KEYS.TECHNICALDOCROTATING)
              : this.i18n.langString(I18N_KEYS.TECHNICALDOC);
      case SettingSubpageEnums.Help:
        return this.i18n.langString(I18N_KEYS.HELPANDSUPPORT);
      case SettingSubpageEnums.HelpAbout:
        return this.i18n.langString(I18N_KEYS.HELPANDSUPPORT);
      case SettingSubpageEnums.HelpFaq:
        return this.i18n.langString(I18N_KEYS.HELPANDSUPPORT);
      case SettingSubpageEnums.Terms:
        return this.i18n.langString(I18N_KEYS.IMPRINT);
      case SettingSubpageEnums.TermsOfService:
        return this.i18n.langString(I18N_KEYS.IMPRINT);
      case SettingSubpageEnums.TermsPrivacyPolicy:
        return 'Privacy policy';
      case SettingSubpageEnums.Imprint:
        return this.i18n.langString(I18N_KEYS.IMPRINT2);
      case SettingSubpageEnums.TermsOfUse:
        return this.i18n.langString(I18N_KEYS.TERMSOFUSE);
      case SettingSubpageEnums.DataProtection:
        return this.i18n.langString(I18N_KEYS.DATAPROTECTION);
      case SettingSubpageEnums.ServiceCondition:
        return this.i18n.langString(I18N_KEYS.SERVICECONDITIONS);
      case SettingSubpageEnums.NoteForUsResidents:
        return this.i18n.langString(I18N_KEYS.NOTEFORUSRESIDENTS);
      default:
        return GLOBAL_CONSTANT.EMPTY_STRING;
    }
  }

  // 6. Refactor methods to use signal setters (.set, .update, .mutate) and getters (signal())
  onNameChange(value: any) {
    this.containName = value ? true : false;
    this.settingsCopyForProfile.update((copy: any) => {
      if (copy) copy.name = value;
      return copy;
    });

    if (value !== this.settings()?.name) {
      this.changes.set(true);
    } else {
      this.checkForChanges();
    }
  }

  onSurnameChange(value: any) {
    this.containSurname = value ? true : false;
    this.settingsCopyForProfile.update((copy: any) => {
      if (copy) {
        copy.surname = value;
      }
      return copy;
    });

    if (value !== this.settings()?.surname) {
      this.changes.set(true);
    } else {
      this.checkForChanges();
    }
  }

  onDesignationChange(value: any) {
    this.settingsCopyForProfile.update((copy: any) => {
      if (copy) copy.designation = value;
      return copy;
    });

    if (this.settings() && value !== this.settings()?.designation) {
      this.changes.set(true);
    } else {
      this.checkForChanges();
    }
  }

  onSubmitProfileChanges() {
    const officialSettings = this.settings();
    const copySettings = this.settingsCopyForProfile();

    if (!this.submitDisabled() && officialSettings && copySettings) {
      // Update the official settings signal with values from the copy
      this.settings.update((s: any) => {
        if (s) {
          s.name = copySettings.name;
          s.surname = copySettings.surname;
          s.designation = copySettings.designation;
          //s.phonenumber = copySettings.phonenumber;
          s.email = copySettings.email;

          // Trigger saving manually here as the old observation$ is gone
          this.success.set(false);
          this.error.set(false);
          this.settingsService.setUserSettings(s).then(() => {
            this.success.set(true);
            this.error.set(false);
          });
        }
        return s;
      });
      this.changes.set(false);
    }
  }

  checkForChanges() {
    const officialSettings = this.settings();
    const copySettings = this.settingsCopyForProfile();

    if (!officialSettings || !copySettings) {
      this.changes.set(false);
      return;
    }

    if (
      officialSettings.name === copySettings.name &&
      officialSettings.surname === copySettings.surname &&
      officialSettings.designation === copySettings.designation &&
      officialSettings.email === copySettings.email
    ) {
      this.changes.set(false);
    } else {
      this.changes.set(true);
    }
  }

  submitDisabled() {
    const copySettings = this.settingsCopyForProfile();
    if (!copySettings || !this.changes()) {
      return true;
    } else return false;

    // if (
    //   !this.changes() ||
    //   copySettings.name === GLOBAL_CONSTANT.EMPTY_STRING ||
    //   copySettings.surname === GLOBAL_CONSTANT.EMPTY_STRING ||
    //   copySettings.email === GLOBAL_CONSTANT.EMPTY_STRING
    // ) {
    //   return true;
    // } else {
    //   return false;
    // }
  }

  toSubpage(subpageUrl: string) {
    this.router.navigate(['settings/' + subpageUrl]);
  }

  toTermsOfService() {
    if (this.i18n.languageSignal() === SupportedLanguage.DE) {
      window.open('https://www.henkel-adhesives.com/de/de/about/terms-of-use.html', '_blank');
    } else {
      window.open('https://www.henkel-adhesives.com/de/en/about/terms-of-use.html', '_blank');
    }
  }

  toPrivacyPolicy() {
    // No link yet provided, will probably be a custom text on the subpage
  }

  toAboutPage() {
    if (this.i18n.languageSignal() === SupportedLanguage.DE) {
      window.open(
        // todo: still no german page available
        'https://www.henkel-adhesives.com/uk/en/industries/industrial-maintenance-repair/mro-loctite-pulse.html',
        '_blank'
      );
    } else {
      window.open(
        'https://www.henkel-adhesives.com/uk/en/industries/industrial-maintenance-repair/mro-loctite-pulse.html',
        '_blank'
      );
    }
  }

  toImprint() {
    if (this.i18n.languageSignal() === SupportedLanguage.DE) {
      window.open('https://www.henkel-adhesives.com/de/de/about/imprint.html', '_blank');
    } else {
      window.open('https://www.henkel-adhesives.com/de/en/about/imprint.html', '_blank');
    }
  }
}
